/**
 * SearchableUserSelect — reusable combobox for picking a user/player from a list.
 * Supports live search (nombre, email, phone), optional role filter, and clear button.
 *
 * Usage:
 *   <SearchableUserSelect
 *      value={id}
 *      onChange={(id, user) => setSelected(id)}
 *      options={players}          // [{id, name, email?, phone?, role?, photo_url?, category?}]
 *      placeholder="Selecciona jugador..."
 *      allowClear
 *      testId="assign-user"
 *   />
 */
import React, { useMemo, useRef, useState, useEffect } from "react";
import { MagnifyingGlass, CaretDown, X, Check } from "@phosphor-icons/react";

export default function SearchableUserSelect({
  value,
  onChange,
  options = [],
  placeholder = "Selecciona...",
  allowClear = false,
  disabled = false,
  testId = "searchable-select",
  renderOption,
  className = "",
  emptyLabel = "No se encontraron resultados",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => { clearTimeout(t); document.removeEventListener("mousedown", onDocClick); };
  }, [open]);

  const selected = useMemo(() => options.find((o) => o.id === value) || null, [options, value]);

  const filtered = useMemo(() => {
    if (!q.trim()) return options.slice(0, 100);
    const needle = q.toLowerCase();
    return options.filter((o) => {
      const hay = `${o.name || ""} ${o.email || ""} ${o.phone || ""} ${o.category || ""} ${o.role || ""}`.toLowerCase();
      return hay.includes(needle);
    }).slice(0, 100);
  }, [options, q]);

  const label = selected ? selected.name : placeholder;

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid={testId}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`w-full h-11 sm:h-10 px-3 flex items-center justify-between bg-[#0A0A0A] border border-white/10 rounded-md text-sm text-left transition-colors hover:border-[#CCFF00]/40 focus:outline-none focus:ring-1 focus:ring-[#CCFF00] disabled:opacity-50 disabled:cursor-not-allowed ${selected ? "text-white" : "text-gray-400"}`}
        data-testid={`${testId}-trigger`}
      >
        <span className="truncate flex-1 flex items-center gap-2">
          {selected?.photo_url && <img src={selected.photo_url} alt="" className="w-6 h-6 rounded-full object-cover" />}
          {label}
          {selected?.category && <span className="shrink-0 px-1.5 py-0.5 rounded bg-[#CCFF00]/20 text-[#CCFF00] text-[10px] font-bold">C{selected.category}</span>}
        </span>
        <div className="flex items-center gap-1 shrink-0 ml-2">
          {allowClear && selected && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onChange?.("", null); }}
              className="p-0.5 rounded hover:bg-white/10 cursor-pointer"
              aria-label="Limpiar"
              data-testid={`${testId}-clear`}
            >
              <X size={14} className="text-gray-400" />
            </span>
          )}
          <CaretDown size={14} className="text-gray-400" />
        </div>
      </button>

      {open && !disabled && (
        <div className="absolute z-[60] mt-1 w-full bg-[#111] border border-white/10 rounded-md shadow-2xl overflow-hidden">
          <div className="p-2 border-b border-white/5 flex items-center gap-2 bg-black/60">
            <MagnifyingGlass size={14} className="text-gray-400 shrink-0" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre, email, teléfono..."
              className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 outline-none"
              data-testid={`${testId}-search`}
            />
            {q && (
              <button type="button" onClick={() => setQ("")} className="p-0.5 rounded hover:bg-white/10" data-testid={`${testId}-search-clear`}>
                <X size={12} className="text-gray-500" />
              </button>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto overscroll-contain" data-testid={`${testId}-list`}>
            {filtered.length === 0 ? (
              <div className="p-4 text-center text-xs text-gray-500">{emptyLabel}</div>
            ) : (
              filtered.map((o) => (
                <button
                  type="button"
                  key={o.id}
                  onClick={() => { onChange?.(o.id, o); setOpen(false); setQ(""); }}
                  className={`w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-[#CCFF00]/10 transition-colors ${o.id === value ? "bg-[#CCFF00]/5" : ""}`}
                  data-testid={`${testId}-option-${o.id}`}
                >
                  {renderOption ? (
                    renderOption(o, o.id === value)
                  ) : (
                    <>
                      {o.photo_url ? (
                        <img src={o.photo_url} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
                      ) : (
                        <span className="w-7 h-7 rounded-full bg-white/10 text-white flex items-center justify-center text-xs font-bold shrink-0">
                          {(o.name || "?").charAt(0).toUpperCase()}
                        </span>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{o.name}</p>
                        {(o.email || o.phone) && <p className="text-[10px] text-gray-500 truncate">{o.email || o.phone}</p>}
                      </div>
                      {o.category && <span className="shrink-0 px-1.5 py-0.5 rounded bg-[#CCFF00]/20 text-[#CCFF00] text-[10px] font-bold">C{o.category}</span>}
                      {o.id === value && <Check size={14} weight="bold" className="text-[#CCFF00] shrink-0" />}
                    </>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
